import { useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Pause, Play, RotateCcw, X } from 'lucide-react'
import type { ReadAloudStatus, TtsVoice } from '@/domains/types'
import { formatClock } from '@/application/tts/listening-time'
import type { ReadAloudEngineChoice } from '@/application/stores/read-aloud.store'
import { ReadAloudVoiceMenu } from './ReadAloudVoiceMenu'
import { ReadAloudConsentPrompt } from './ReadAloudConsentPrompt'
import { formatSpokenDuration } from './formatSpokenDuration'
import { readAloudIconButtonClass } from './readAloudClasses'

export interface ReadAloudBarProps {
  variant: 'inline' | 'sticky'
  status: ReadAloudStatus
  title: string
  restingDurationMinutes: number
  segmentIndex: number
  segmentCount: number
  elapsedSeconds: number
  totalSeconds: number
  rate: number
  voices: TtsVoice[]
  primaryLang: string
  selectedVoiceId: string | null
  lowQualityOnly: boolean
  toggle: () => void
  stop: () => void
  seekToSegment: (index: number) => void
  cycleRate: () => void
  setVoice: (voiceId: string | null) => void
  preparingProgress?: number | null
  enginePreference?: ReadAloudEngineChoice
  siteAvailable?: boolean
  deviceAvailable?: boolean
  consentPending?: boolean
  usingFallback?: boolean
  acceptModelDownload?: () => void
  declineModelDownload?: () => void
  setEnginePreference?: (choice: ReadAloudEngineChoice) => void
}

type RangeStyle = React.CSSProperties & { '--read-aloud-progress': number }

/**
 * Presentational read-aloud control bar — three visual shapes driven purely
 * by `status` (read-mode.listen spec §3): a compact pill at rest or once
 * finished (`idle`/`loading`/`ended`/`error`), and a full transport bar with
 * seek/time/speed/menu/stop while `playing`/`paused`. `variant="sticky"`
 * renders the same active-state bar but drops the "⋯" voice menu and
 * truncates/hides the title on very narrow viewports, per the mockup.
 */
export function ReadAloudBar({
  variant,
  status,
  title,
  restingDurationMinutes,
  segmentIndex,
  segmentCount,
  elapsedSeconds,
  totalSeconds,
  rate,
  voices,
  primaryLang,
  selectedVoiceId,
  lowQualityOnly,
  toggle,
  stop,
  seekToSegment,
  cycleRate,
  setVoice,
  preparingProgress = null,
  enginePreference = 'device',
  siteAvailable = false,
  deviceAvailable = true,
  consentPending = false,
  usingFallback = false,
  acceptModelDownload = () => {},
  declineModelDownload = () => {},
  setEnginePreference = () => {},
}: ReadAloudBarProps) {
  const { t, i18n } = useTranslation()
  const [pendingIndex, setPendingIndex] = useState<number | null>(null)

  const numberFormat = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  )

  // A `preparing` state past the first segment (a later-language voice
  // loading mid-article) keeps the transport bar instead of collapsing it.
  const isPreparing = status === 'preparing'
  const isActive = status === 'playing' || status === 'paused' || (isPreparing && segmentIndex > 0)
  const isFinished = status === 'ended' || status === 'error'

  const regionLabel = t('read_mode.listen.region_label')

  const containerClass =
    variant === 'sticky'
      ? 'read-aloud read-aloud--sticky flex flex-wrap items-center gap-2 rounded-full border border-input bg-card px-3 py-2 shadow-lg print:hidden'
      : 'read-aloud flex flex-wrap items-center gap-2 rounded-full border border-input bg-background px-3 py-2 print:hidden'

  const statusAnnouncement =
    status === 'playing'
      ? t('read_mode.listen.status_playing')
      : status === 'paused'
        ? t('read_mode.listen.status_paused')
        : status === 'ended'
          ? t('read_mode.listen.status_ended')
          : status === 'error'
            ? t('read_mode.listen.error')
            : ''

  const preparingLabel =
    preparingProgress === null
      ? t('read_mode.listen.preparing')
      : t('read_mode.listen.preparing_percent', { percent: Math.round(preparingProgress * 100) })

  const voiceMenu = (
    <ReadAloudVoiceMenu
      voices={voices}
      primaryLang={primaryLang}
      selectedVoiceId={selectedVoiceId}
      onSelectVoice={setVoice}
      lowQualityOnly={lowQualityOnly}
      enginePreference={enginePreference}
      siteAvailable={siteAvailable}
      deviceAvailable={deviceAvailable}
      usingFallback={usingFallback}
      onSelectEngine={setEnginePreference}
    />
  )

  if (!isActive) {
    const label = isFinished
      ? t('read_mode.listen.replay')
      : isPreparing
        ? preparingLabel
        : t('read_mode.listen.play_label', {
            duration: t('read_mode.listen.duration', { count: restingDurationMinutes }),
          })

    return (
      <div role="region" aria-label={regionLabel} className="read-aloud-group flex flex-col gap-2">
        <div className={containerClass}>
          <button
            type="button"
            onClick={toggle}
            disabled={status === 'loading'}
            aria-label={label}
            className={readAloudIconButtonClass}
          >
            {status === 'loading' || isPreparing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : isFinished ? (
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Play className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <span className="truncate text-sm font-medium text-foreground">
            {isPreparing ? (
              preparingLabel
            ) : (
              <>
                {isFinished ? t('read_mode.listen.replay') : t('read_mode.listen.cta')}
                {' · '}
                {t('read_mode.listen.duration', { count: restingDurationMinutes })}
              </>
            )}
          </span>
          {status === 'error' && (
            <span className="text-xs text-destructive">{t('read_mode.listen.error')}</span>
          )}
          {usingFallback && status !== 'error' && (
            <span className="text-xs text-muted-foreground">
              {t('read_mode.listen.fallback_notice')}
            </span>
          )}
          {siteAvailable && voiceMenu}
          <span aria-live="polite" className="sr-only">
            {statusAnnouncement}
          </span>
        </div>
        {consentPending && (
          <ReadAloudConsentPrompt onAccept={acceptModelDownload} onDecline={declineModelDownload} />
        )}
      </div>
    )
  }

  const displayIndex = pendingIndex ?? segmentIndex
  const progress = segmentCount > 1 ? displayIndex / (segmentCount - 1) : 0
  const rangeStyle: RangeStyle = { '--read-aloud-progress': progress }

  const commitSeek = () => {
    if (pendingIndex !== null) {
      seekToSegment(pendingIndex)
      setPendingIndex(null)
    }
  }

  const seekValueText = t('read_mode.listen.position_text', {
    elapsed: formatSpokenDuration(elapsedSeconds, i18n.language, t),
    total: formatSpokenDuration(totalSeconds, i18n.language, t),
  })

  return (
    <div role="region" aria-label={regionLabel} className={containerClass}>
      <button
        type="button"
        onClick={toggle}
        aria-label={
          status === 'playing' || isPreparing
            ? t('read_mode.listen.pause')
            : t('read_mode.listen.resume')
        }
        className={readAloudIconButtonClass}
      >
        {isPreparing ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : status === 'playing' ? (
          <Pause className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Play className="h-4 w-4" aria-hidden="true" />
        )}
      </button>

      {variant === 'sticky' && (
        <span className="hidden min-w-0 truncate text-sm font-medium text-foreground min-[400px]:inline">
          {title}
        </span>
      )}

      <input
        type="range"
        min={0}
        max={Math.max(segmentCount - 1, 0)}
        step={1}
        value={displayIndex}
        aria-label={t('read_mode.listen.seek_label')}
        aria-valuetext={seekValueText}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setPendingIndex(Number(event.target.value))
        }
        onMouseUp={commitSeek}
        onTouchEnd={commitSeek}
        onKeyUp={commitSeek}
        style={rangeStyle}
        className="read-aloud__seek min-w-0 flex-1"
      />

      <span className="shrink-0 text-xs tabular-nums text-muted-foreground sm:text-sm">
        {isPreparing
          ? preparingLabel
          : `${formatClock(elapsedSeconds)} / ≈${formatClock(totalSeconds)}`}
      </span>

      <button
        type="button"
        onClick={cycleRate}
        aria-label={t('read_mode.listen.speed_label', { rate: numberFormat.format(rate) })}
        className={`${readAloudIconButtonClass} w-auto min-w-10 px-2 text-xs font-semibold`}
      >
        {numberFormat.format(rate)}×
      </button>

      {variant === 'inline' && voiceMenu}

      <button
        type="button"
        onClick={stop}
        aria-label={t('read_mode.listen.stop')}
        className={readAloudIconButtonClass}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      <span aria-live="polite" className="sr-only">
        {statusAnnouncement}
      </span>
    </div>
  )
}
